// generatePublicZip.ts - Complete version with cloud function support

import { Functions } from 'firebase/functions';
import { App } from '../AppManager';
import { generateHTML } from './generateHTML';
import { generateManifest } from './generateManifest';
import { generateRobots } from './generateRobots';
import { generateSitemap } from './generateSitemap';
import { generateThemeConfig } from './generateThemeConfig';
import { createImageProcessor, ImageSizes } from './imageProcessor';

interface PublicZipGeneratorOptions {
  appData: App;
  customDomain?: string;
  functions?: Functions; // Add functions support
}

// Enhanced ZIP creator with proper ZIP file structure
class ZipCreator {
  private files: { name: string; content: Uint8Array; isText: boolean }[] = [];

  addFile(name: string, content: string) {
    console.log(`📄 Adding text file: ${name} (${content.length} chars)`);
    const encoder = new TextEncoder();
    const encoded = encoder.encode(content);
    this.files.push({
      name,
      content: encoded,
      isText: true
    });
    console.log(`✅ Added text file: ${name} (${encoded.length} bytes)`);
  }

  addImageFile(name: string, dataURL: string) {
    console.log(`🖼️ Processing image file: ${name}`);
    
    if (!dataURL || !dataURL.startsWith('data:image/')) {
      console.error(`❌ ${name}: Invalid data URL`);
      return;
    }
    
    try {
      const base64Data = dataURL.split(',')[1];
      if (!base64Data) {
        console.error(`❌ ${name}: No base64 data found`);
        return;
      }
      
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      this.files.push({
        name,
        content: bytes,
        isText: false
      });
      
      console.log(`✅ Added image file: ${name} (${bytes.length} bytes)`);
    } catch (error) {
      console.error(`❌ Error processing image ${name}:`, error);
    }
  }

  // Calculate CRC32 checksum
  private calculateCRC32(data: Uint8Array): number {
    const crcTable = this.makeCRCTable();
    let crc = 0 ^ (-1);

    for (let i = 0; i < data.length; i++) {
      crc = (crc >>> 8) ^ crcTable[(crc ^ data[i]) & 0xFF];
    }

    return (crc ^ (-1)) >>> 0;
  }

  private makeCRCTable(): number[] {
    let c: number;
    const crcTable: number[] = [];
    for (let n = 0; n < 256; n++) {
      c = n;
      for (let k = 0; k < 8; k++) {
        c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
      }
      crcTable[n] = c;
    }
    return crcTable;
  }

  generate(): Blob {
    console.log(`🔧 Generating ZIP with ${this.files.length} files`);

    const chunks: Uint8Array[] = [];
    const centralDirectory: Uint8Array[] = [];
    let offset = 0;

    // Process each file
    this.files.forEach((file) => {
      const crc32 = this.calculateCRC32(file.content);
      
      // Create local file header
      const localHeader = this.createLocalFileHeader(file.name, file.content.length, crc32);
      chunks.push(localHeader);
      chunks.push(file.content);
      
      // Create central directory entry
      const centralDirEntry = this.createCentralDirectoryEntry(
        file.name, 
        file.content.length, 
        crc32,
        offset
      );
      centralDirectory.push(centralDirEntry);
      
      offset += localHeader.length + file.content.length;
    });

    // Add central directory
    centralDirectory.forEach(entry => chunks.push(entry));

    // Add end of central directory
    const centralDirSize = centralDirectory.reduce((sum, entry) => sum + entry.length, 0);
    const endOfCentralDir = this.createEndOfCentralDirectory(
      this.files.length, 
      centralDirSize, 
      offset
    );
    chunks.push(endOfCentralDir);

    // Combine all chunks
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const result = new Uint8Array(totalLength);
    let pos = 0;
    chunks.forEach(chunk => {
      result.set(chunk, pos);
      pos += chunk.length;
    });

    console.log('✅ ZIP file generated successfully');
    return new Blob([result], { type: 'application/zip' });
  }

  private createLocalFileHeader(fileName: string, fileSize: number, crc32: number): Uint8Array {
    const nameBytes = new TextEncoder().encode(fileName);
    const header = new Uint8Array(30 + nameBytes.length);
    
    // Local file header signature
    header[0] = 0x50; header[1] = 0x4b; header[2] = 0x03; header[3] = 0x04;
    
    // Version needed to extract
    header[4] = 0x14; header[5] = 0x00;
    
    // General purpose bit flag
    header[6] = 0x00; header[7] = 0x00;
    
    // Compression method (0 = no compression)
    header[8] = 0x00; header[9] = 0x00;
    
    // Last mod time/date
    const now = new Date();
    const dosTime = this.dateToDosTime(now);
    const dosDate = this.dateToDosDate(now);
    header[10] = dosTime & 0xff; header[11] = (dosTime >> 8) & 0xff;
    header[12] = dosDate & 0xff; header[13] = (dosDate >> 8) & 0xff;
    
    // CRC-32
    header[14] = crc32 & 0xff;
    header[15] = (crc32 >> 8) & 0xff;
    header[16] = (crc32 >> 16) & 0xff;
    header[17] = (crc32 >> 24) & 0xff;
    
    // Compressed size
    header[18] = fileSize & 0xff;
    header[19] = (fileSize >> 8) & 0xff;
    header[20] = (fileSize >> 16) & 0xff;
    header[21] = (fileSize >> 24) & 0xff;
    
    // Uncompressed size
    header[22] = fileSize & 0xff;
    header[23] = (fileSize >> 8) & 0xff;
    header[24] = (fileSize >> 16) & 0xff;
    header[25] = (fileSize >> 24) & 0xff;
    
    // File name length
    header[26] = nameBytes.length & 0xff;
    header[27] = (nameBytes.length >> 8) & 0xff;
    
    // Extra field length
    header[28] = 0x00; header[29] = 0x00;
    
    // File name
    header.set(nameBytes, 30);
    
    return header;
  }

  private createCentralDirectoryEntry(fileName: string, fileSize: number, crc32: number, offset: number): Uint8Array {
    const nameBytes = new TextEncoder().encode(fileName);
    const entry = new Uint8Array(46 + nameBytes.length);
    
    // Central directory signature
    entry[0] = 0x50; entry[1] = 0x4b; entry[2] = 0x01; entry[3] = 0x02;
    
    // Version made by
    entry[4] = 0x14; entry[5] = 0x00;
    
    // Version needed to extract
    entry[6] = 0x14; entry[7] = 0x00;
    
    // General purpose bit flag
    entry[8] = 0x00; entry[9] = 0x00;
    
    // Compression method
    entry[10] = 0x00; entry[11] = 0x00;
    
    // Last mod time/date
    const now = new Date();
    const dosTime = this.dateToDosTime(now);
    const dosDate = this.dateToDosDate(now);
    entry[12] = dosTime & 0xff; entry[13] = (dosTime >> 8) & 0xff;
    entry[14] = dosDate & 0xff; entry[15] = (dosDate >> 8) & 0xff;
    
    // CRC-32
    entry[16] = crc32 & 0xff;
    entry[17] = (crc32 >> 8) & 0xff;
    entry[18] = (crc32 >> 16) & 0xff;
    entry[19] = (crc32 >> 24) & 0xff;
    
    // Compressed size
    entry[20] = fileSize & 0xff;
    entry[21] = (fileSize >> 8) & 0xff;
    entry[22] = (fileSize >> 16) & 0xff;
    entry[23] = (fileSize >> 24) & 0xff;
    
    // Uncompressed size
    entry[24] = fileSize & 0xff;
    entry[25] = (fileSize >> 8) & 0xff;
    entry[26] = (fileSize >> 16) & 0xff;
    entry[27] = (fileSize >> 24) & 0xff;
    
    // File name length
    entry[28] = nameBytes.length & 0xff;
    entry[29] = (nameBytes.length >> 8) & 0xff;
    
    // Extra field length
    entry[30] = 0x00; entry[31] = 0x00;
    
    // File comment length
    entry[32] = 0x00; entry[33] = 0x00;
    
    // Disk number start
    entry[34] = 0x00; entry[35] = 0x00;
    
    // Internal file attributes
    entry[36] = 0x00; entry[37] = 0x00;
    
    // External file attributes
    entry[38] = 0x00; entry[39] = 0x00; entry[40] = 0x00; entry[41] = 0x00;
    
    // Relative offset of local header
    entry[42] = offset & 0xff;
    entry[43] = (offset >> 8) & 0xff;
    entry[44] = (offset >> 16) & 0xff;
    entry[45] = (offset >> 24) & 0xff;
    
    // File name
    entry.set(nameBytes, 46);
    
    return entry;
  }

  private createEndOfCentralDirectory(fileCount: number, centralDirSize: number, centralDirOffset: number): Uint8Array {
    const eocd = new Uint8Array(22);
    
    // End of central directory signature
    eocd[0] = 0x50; eocd[1] = 0x4b; eocd[2] = 0x05; eocd[3] = 0x06;
    
    // Number of this disk
    eocd[4] = 0x00; eocd[5] = 0x00;
    
    // Disk where central directory starts
    eocd[6] = 0x00; eocd[7] = 0x00;
    
    // Number of central directory records on this disk
    eocd[8] = fileCount & 0xff;
    eocd[9] = (fileCount >> 8) & 0xff;
    
    // Total number of central directory records
    eocd[10] = fileCount & 0xff;
    eocd[11] = (fileCount >> 8) & 0xff;
    
    // Size of central directory
    eocd[12] = centralDirSize & 0xff;
    eocd[13] = (centralDirSize >> 8) & 0xff;
    eocd[14] = (centralDirSize >> 16) & 0xff;
    eocd[15] = (centralDirSize >> 24) & 0xff;
    
    // Offset of start of central directory
    eocd[16] = centralDirOffset & 0xff;
    eocd[17] = (centralDirOffset >> 8) & 0xff;
    eocd[18] = (centralDirOffset >> 16) & 0xff;
    eocd[19] = (centralDirOffset >> 24) & 0xff;
    
    // ZIP file comment length
    eocd[20] = 0x00; eocd[21] = 0x00;
    
    return eocd;
  }

  private dateToDosTime(date: Date): number {
    return (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1);
  }

  private dateToDosDate(date: Date): number {
    return ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  }
}

// Main function to generate ZIP with cloud function support
export const generatePublicZip = async ({ appData, customDomain, functions }: PublicZipGeneratorOptions): Promise<Blob> => {
  console.log('🚀 Starting public files generation with cloud function support');
  
  // Generate all the text files first
  const htmlContent = generateHTML({ appData, customDomain });
  const manifestContent = JSON.stringify(generateManifest({ appData }), null, 2);
  const robotsContent = generateRobots({ appData, customDomain });
  const sitemapContent = generateSitemap({ appData, customDomain });
  const { content: themeConfigContent, filename: themeConfigFilename } = generateThemeConfig({ appData });
  
  // Attempt image generation with cloud function support
  let imageGenerationResult: {
    success: boolean;
    imageSizes?: ImageSizes;
    error?: string;
    method?: string;
  } = { success: false };

  if (appData.appIcon) {
    console.log('🎨 Attempting image generation with cloud function support...');
    
    try {
      const imageProcessor = createImageProcessor(functions);
      const imageSizes = await imageProcessor.generateImageSizesFromUrl(appData.appIcon, appData.id);
      imageGenerationResult = { 
        success: true, 
        imageSizes,
        method: 'cloud-function'
      };
      console.log('✅ Successfully generated all image sizes via cloud function');
    } catch (error) {
      console.warn('⚠️ Cloud function image generation failed:', error);
      imageGenerationResult = { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error',
        method: 'failed'
      };
    }
  } else {
    console.log('ℹ️ No app icon configured, skipping image generation');
    imageGenerationResult = { success: false, method: 'no-icon' };
  }
  
  // Create appropriate README based on results
  const readmeContent = generateReadmeContent(appData, customDomain, imageGenerationResult);
  
  // Create ZIP file
  const zip = new ZipCreator();
  zip.addFile('README.txt', readmeContent);
  zip.addFile('index.html', htmlContent);
  zip.addFile('manifest.json', manifestContent);
  zip.addFile('robots.txt', robotsContent);
  zip.addFile('sitemap.xml', sitemapContent);
  zip.addFile(themeConfigFilename, themeConfigContent);

  // Add images if successfully generated
  if (imageGenerationResult.success && imageGenerationResult.imageSizes) {
    console.log('🖼️ Adding generated image files to ZIP...');
    
    const imageFiles = [
      { name: 'favicon.ico', dataURL: imageGenerationResult.imageSizes.favicon32 },
      { name: 'favicon.png', dataURL: imageGenerationResult.imageSizes.favicon32 },
      { name: 'favicon-16x16.png', dataURL: imageGenerationResult.imageSizes.favicon16 },
      { name: 'favicon-32x32.png', dataURL: imageGenerationResult.imageSizes.favicon32 },
      { name: 'android-chrome-192x192.png', dataURL: imageGenerationResult.imageSizes.favicon192 },
      { name: 'android-chrome-512x512.png', dataURL: imageGenerationResult.imageSizes.favicon512 },
      { name: 'apple-touch-icon.png', dataURL: imageGenerationResult.imageSizes.appleTouchIcon },
      { name: 'metaimg.png', dataURL: imageGenerationResult.imageSizes.socialMediaImage }
    ];

    imageFiles.forEach(imageFile => {
      if (imageFile.dataURL && imageFile.dataURL.startsWith('data:image/')) {
        zip.addImageFile(imageFile.name, imageFile.dataURL);
      }
    });
  } else if (imageGenerationResult.method === 'failed' && appData.appIcon) {
    // Add troubleshooting instructions for failed generation
    console.log('📋 Adding troubleshooting instructions');
    const troubleshootingInstructions = generateTroubleshootingInstructions(appData, imageGenerationResult.error);
    zip.addFile('TROUBLESHOOTING.txt', troubleshootingInstructions);
  }

  console.log('📦 Generating ZIP file...');
  return zip.generate();
};

// Generate appropriate README content based on image generation results
function generateReadmeContent(
  appData: App, 
  customDomain: string | undefined, 
  imageResult: { success: boolean; imageSizes?: ImageSizes; error?: string; method?: string }
): string {
  const baseContent = `# Public Folder Files for ${appData.name}

This ZIP contains the following files for your React app's public folder:

## Files included:

### 1. index.html
Customized HTML file with:
- App-specific meta tags and SEO
- Theme color integration
- Social media tags (Open Graph, Twitter Cards)
- Structured data for search engines
- Dine Platform attribution

### 2. manifest.json
PWA manifest file with:
- App branding and icons
- Theme-appropriate colors
- Mobile app installation support

### 3. robots.txt
SEO optimization file with:
- Search engine directives
- Sitemap reference pointing to your domain
- Access permissions

### 4. sitemap.xml
XML sitemap for search engines with:
- Common app pages (/, /menu, /cart, /orders, /contact, /about)
- URLs pointing to your configured domain
- Proper priorities and change frequencies
- Current date timestamps

${getImageSectionContent(imageResult, appData)}

## Installation Instructions:

1. Extract all files from this ZIP
2. Copy all files to your React app's public/ folder
3. Replace existing files if they exist
${getImageInstructions(imageResult)}
4. Test your app to ensure everything works correctly

## App Details:
- App Name: ${appData.name}
- App ID: ${appData.id}
- Domain: ${customDomain || `${appData.id}.web.app`}
- Generated: ${new Date().toISOString()}
- Image Generation: ${getImageStatusText(imageResult)}

${getAdditionalInfo(imageResult)}

Powered by Dine Platform - https://www.dineplatform.com
`;

  return baseContent;
}

function getImageSectionContent(
  imageResult: { success: boolean; method?: string }, 
  appData: App
): string {
  if (!appData.appIcon) {
    return `### 5. App Icon Files
❌ No app icon configured. Please upload an app icon in the workflow to generate favicon and social media images.
`;
  }

  if (imageResult.success) {
    const methodText = imageResult.method === 'cloud-function' 
      ? 'Generated via cloud function proxy (CORS-free)'
      : 'Generated successfully';
      
    return `### 5. App Icon Files (${methodText} ✅)
Generated from your uploaded app icon:
- favicon.ico (32x32)
- favicon.png (32x32)
- favicon-16x16.png (16x16)
- favicon-32x32.png (32x32)
- android-chrome-192x192.png (192x192)
- android-chrome-512x512.png (512x512)
- apple-touch-icon.png (180x180)
- metaimg.png (1200x630 for social media)

`;
  }

  return `### 5. App Icon Files (Generation Failed ❌)
Image generation failed. See TROUBLESHOOTING.txt for detailed instructions on how to:
- Generate images manually
- Fix cloud function issues
- Configure CORS settings
- Use alternative generation methods

Required files:
- favicon.ico (32x32)
- favicon.png (32x32)
- favicon-16x16.png (16x16)
- favicon-32x32.png (32x32)
- android-chrome-192x192.png (192x192)
- android-chrome-512x512.png (512x512)
- apple-touch-icon.png (180x180)
- metaimg.png (1200x630 for social media)

`;
}

function getImageInstructions(imageResult: { success: boolean; method?: string }): string {
  if (imageResult.success) {
    return "3. All icon files are included and ready to use\n";
  }
  
  return "3. Follow the troubleshooting instructions in TROUBLESHOOTING.txt to generate icon files\n";
}

function getImageStatusText(imageResult: { success: boolean; method?: string; error?: string }): string {
  if (imageResult.success) {
    return imageResult.method === 'cloud-function' 
      ? '✅ Generated via cloud function proxy'
      : '✅ Generated successfully';
  }
  
  if (imageResult.method === 'no-icon') return '❌ No app icon configured';
  return `❌ Generation failed - ${imageResult.error || 'Unknown error'}`;
}

function getAdditionalInfo(imageResult: { success: boolean; method?: string; error?: string }): string {
  if (imageResult.success && imageResult.method === 'cloud-function') {
    return `
## Cloud Function Proxy:
This package was generated using a cloud function proxy to bypass CORS restrictions.
This is the preferred method as it eliminates browser security limitations while maintaining full security.
`;
  }

  if (!imageResult.success && imageResult.method === 'failed') {
    return `
## Image Generation Failed:
The cloud function proxy encountered an issue. Please check:
1. Your Firebase Functions are deployed and accessible
2. You have proper authentication
3. The app icon URL is valid and accessible
4. Your permissions are correctly configured

See TROUBLESHOOTING.txt for detailed resolution steps.
`;
  }
  
  return '';
}

// Generate troubleshooting instructions
function generateTroubleshootingInstructions(appData: App, error?: string): string {
  return `# Image Generation Troubleshooting for ${appData.name}

## Issue Summary
Image generation failed for your app icon. This document provides step-by-step solutions.

## Error Details
${error ? `Error: ${error}` : 'No specific error details available'}

App Icon URL: ${appData.appIcon || 'Not configured'}

## Solution 1: Verify Cloud Function Deployment

1. **Check if fetchImageForProcessing function is deployed:**
   - Go to Firebase Console → Functions
   - Look for 'fetchImageForProcessing' function
   - Ensure it's deployed and not showing errors

2. **Redeploy if necessary:**
   \`\`\`bash
   firebase deploy --only functions:fetchImageForProcessing
   \`\`\`

## Solution 2: Check Authentication
- Ensure you're signed in to the correct Firebase project
- Verify your user has proper permissions for the app
- Try signing out and back in

## Solution 3: Verify App Icon URL
- Check that the app icon URL is valid
- Ensure it points to a Firebase Storage file
- Try accessing the URL directly in a browser

## Solution 4: Manual Image Generation

### Online Tools (Recommended)
1. Go to https://realfavicongenerator.net/
2. Upload your app icon
3. Download the generated favicon package
4. Extract and rename files according to the list below

### Required Files and Dimensions:
- **favicon.ico** - 32x32 pixels (ICO format)
- **favicon.png** - 32x32 pixels (PNG format)  
- **favicon-16x16.png** - 16x16 pixels
- **favicon-32x32.png** - 32x32 pixels
- **android-chrome-192x192.png** - 192x192 pixels
- **android-chrome-512x512.png** - 512x512 pixels
- **apple-touch-icon.png** - 180x180 pixels
- **metaimg.png** - 1200x630 pixels (for social media)

### Using ImageMagick (Command Line)
If you have ImageMagick installed:

\`\`\`bash
# Download your app icon first, then run:
convert app-icon.png -resize 16x16 favicon-16x16.png
convert app-icon.png -resize 32x32 favicon-32x32.png
convert app-icon.png -resize 32x32 favicon.png
convert app-icon.png -resize 32x32 favicon.ico
convert app-icon.png -resize 192x192 android-chrome-192x192.png
convert app-icon.png -resize 512x512 android-chrome-512x512.png
convert app-icon.png -resize 180x180 apple-touch-icon.png
convert app-icon.png -resize 1200x630 metaimg.png
\`\`\`

## Solution 5: Alternative Cloud Function Approach
If the main cloud function isn't working, you can create a simple HTTP endpoint:

\`\`\`javascript
// Create a new function that returns the image as base64
exports.getAppIcon = functions.https.onRequest(async (req, res) => {
  // Add your image fetching logic here
  // Return as base64 data URL
});
\`\`\`

## Solution 6: CORS Configuration (Fallback)
As a last resort, configure CORS for Firebase Storage:

1. Create cors.json:
\`\`\`json
[
  {
    "origin": ["*"],
    "method": ["GET"],
    "maxAgeSeconds": 3600
  }
]
\`\`\`

2. Apply CORS:
\`\`\`bash
gsutil cors set cors.json gs://your-bucket-name
\`\`\`

## Getting Help
If none of these solutions work:
1. Check the browser console for detailed error messages
2. Verify your Firebase project configuration
3. Contact support with the specific error details

Generated on: ${new Date().toISOString()}
App ID: ${appData.id}
`;
}

// Main download function with cloud function support
export const downloadPublicFiles = async ({ appData, customDomain, functions }: PublicZipGeneratorOptions): Promise<void> => {
  try {
    console.log('🚀 Starting public files download with cloud function support');
    
    // Generate the ZIP file with cloud function support
    const zipBlob = await generatePublicZip({ appData, customDomain, functions });
    const zipName = `public-files-${appData.id}.zip`;
    
    // Download the ZIP file
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = zipName;
    link.click();
    URL.revokeObjectURL(url);
    
    console.log('✅ ZIP file downloaded successfully');
    
  } catch (error) {
    console.error('❌ Error creating ZIP file:', error);
    
    // Comprehensive fallback strategy
    console.log('🔄 Implementing fallback strategy...');
    
    try {
      await downloadIndividualFiles({ appData, customDomain, functions });
    } catch (fallbackError) {
      console.error('❌ Fallback also failed:', fallbackError);
      throw new Error(`Both ZIP generation and fallback failed. Original error: ${error}. Fallback error: ${fallbackError}`);
    }
  }
};

// Fallback: Download individual files
async function downloadIndividualFiles({ appData, customDomain, functions }: PublicZipGeneratorOptions): Promise<void> {
  console.log('📁 Downloading individual files as fallback...');
  
  // Generate all text files
  const htmlContent = generateHTML({ appData, customDomain });
  const manifestContent = JSON.stringify(generateManifest({ appData }), null, 2);
  const robotsContent = generateRobots({ appData, customDomain });
  const sitemapContent = generateSitemap({ appData, customDomain });
  const { content: themeConfigContent, filename: themeConfigFilename } = generateThemeConfig({ appData });
  
  const files = [
    { name: 'index.html', content: htmlContent },
    { name: 'manifest.json', content: manifestContent },
    { name: 'robots.txt', content: robotsContent },
    { name: 'sitemap.xml', content: sitemapContent },
    { name: themeConfigFilename, content: themeConfigContent }
  ];
  
  // Download each text file
  files.forEach(file => {
    const blob = new Blob([file.content], { 
      type: file.name.endsWith('.html') ? 'text/html' : 
            file.name.endsWith('.json') ? 'application/json' :
            file.name.endsWith('.xml') ? 'application/xml' : 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    URL.revokeObjectURL(url);
  });

  // Try to generate and download images using cloud function
  if (appData.appIcon && functions) {
    try {
      console.log('🎨 Attempting individual image generation via cloud function...');
      const imageProcessor = createImageProcessor(functions);
      const imageSizes = await imageProcessor.generateImageSizesFromUrl(appData.appIcon, appData.id);
      const imageFiles = imageProcessor.generateImageFiles(imageSizes);
      
      imageFiles.forEach(({ name, blob }) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.click();
        URL.revokeObjectURL(url);
      });
      
      console.log('✅ Individual image files downloaded successfully via cloud function');
    } catch (imageError) {
      console.warn('⚠️ Cloud function image generation failed, downloading troubleshooting instructions');
      
      // Download troubleshooting instructions
      const troubleshootingInstructions = generateTroubleshootingInstructions(appData, 
        imageError instanceof Error ? imageError.message : 'Unknown error');
      const instructionsBlob = new Blob([troubleshootingInstructions], { type: 'text/plain' });
      const instructionsUrl = URL.createObjectURL(instructionsBlob);
      const instructionsLink = document.createElement('a');
      instructionsLink.href = instructionsUrl;
      instructionsLink.download = 'TROUBLESHOOTING.txt';
      instructionsLink.click();
      URL.revokeObjectURL(instructionsUrl);
    }
  }

  // Download summary README
  const readmeContent = generateReadmeContent(appData, customDomain, { 
    success: false, 
    method: 'fallback-download',
    error: 'Individual file download fallback'
  });
  const readmeBlob = new Blob([readmeContent], { type: 'text/plain' });
  const readmeUrl = URL.createObjectURL(readmeBlob);
  const readmeLink = document.createElement('a');
  readmeLink.href = readmeUrl;
  readmeLink.download = `README-${appData.id}.txt`;
  readmeLink.click();
  URL.revokeObjectURL(readmeUrl);
}

// Utility function to test if images can be generated with cloud function
export const testImageGeneration = async (appData: App, functions?: Functions): Promise<boolean> => {
  if (!appData.appIcon) {
    console.log('No app icon configured for testing');
    return false;
  }
  
  if (!functions) {
    console.log('No Firebase Functions available for testing');
    return false;
  }
  
  try {
    console.log('🧪 Testing image generation via cloud function:', appData.appIcon);
    const imageProcessor = createImageProcessor(functions);
    const testResult = await imageProcessor.testImageProcessing(appData.appIcon, appData.id);
    
    if (testResult.success && testResult.method === 'cloud-function') {
      console.log('✅ Cloud function image generation test passed');
      return true;
    } else {
      console.log(`⚠️ Image generation test failed or using fallback method: ${testResult.method}`);
      return false;
    }
  } catch (error) {
    console.error('❌ Image generation test failed:', error);
    return false;
  }
};

// Function to check cloud function status and provide helpful information
export const checkCloudFunctionStatus = async (appData: App, functions?: Functions): Promise<{
  available: boolean;
  status: 'no-functions' | 'no-icon' | 'cloud-function-ready' | 'cloud-function-failed' | 'fallback-available';
  message: string;
  method?: string;
  error?: string;
}> => {
  if (!functions) {
    return {
      available: false,
      status: 'no-functions',
      message: 'Firebase Functions not initialized. Cloud function proxy unavailable.'
    };
  }

  if (!appData.appIcon) {
    return {
      available: false,
      status: 'no-icon',
      message: 'No app icon configured. Upload an app icon to enable image generation.'
    };
  }

  try {
    const imageProcessor = createImageProcessor(functions);
    const testResult = await imageProcessor.testImageProcessing(appData.appIcon, appData.id);
    
    if (testResult.success) {
      if (testResult.method === 'cloud-function') {
        return {
          available: true,
          status: 'cloud-function-ready',
          message: 'Cloud function proxy is ready and working perfectly.',
          method: 'cloud-function'
        };
      } else {
        return {
          available: true,
          status: 'fallback-available',
          message: 'Cloud function unavailable, but direct access is working.',
          method: testResult.method
        };
      }
    } else {
      return {
        available: false,
        status: 'cloud-function-failed',
        message: 'Both cloud function and direct access failed.',
        error: testResult.error
      };
    }
  } catch (error) {
    return {
      available: false,
      status: 'cloud-function-failed',
      message: 'Error testing cloud function availability.',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Function to generate individual image files using cloud function
export const generateIndividualImages = async (appData: App, functions?: Functions): Promise<{ name: string; blob: Blob }[]> => {
  if (!appData.appIcon) {
    throw new Error('No app icon configured');
  }
  
  if (!functions) {
    throw new Error('Firebase Functions not initialized. Cannot use cloud function proxy.');
  }
  
  console.log('🎨 Generating individual image files via cloud function');
  
  try {
    const imageProcessor = createImageProcessor(functions);
    const imageSizes = await imageProcessor.generateImageSizesFromUrl(appData.appIcon, appData.id);
    return imageProcessor.generateImageFiles(imageSizes);
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('unauthenticated')) {
        throw new Error('Authentication required. Please sign in to generate images.');
      } else if (error.message.includes('permission-denied')) {
        throw new Error('Permission denied. You do not have access to this app.');
      } else if (error.message.includes('not-found')) {
        throw new Error('App not found or cloud function not deployed.');
      }
    }
    throw new Error(`Cloud function image generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Helper function to download cloud function logs for debugging
export const downloadCloudFunctionLogs = (appData: App, error?: string): void => {
  const logContent = `# Cloud Function Debug Log for ${appData.name}

## Timestamp
${new Date().toISOString()}

## App Details
- App ID: ${appData.id}
- App Name: ${appData.name}
- App Icon URL: ${appData.appIcon || 'Not configured'}

## Error Information
${error || 'No specific error provided'}

## Debugging Steps

1. **Check Function Deployment:**
   - Go to Firebase Console → Functions
   - Verify 'fetchImageForProcessing' is deployed
   - Check function logs for errors

2. **Test Function Manually:**
   - Use Firebase CLI: firebase functions:shell
   - Call function directly with test data

3. **Verify Permissions:**
   - Check user authentication
   - Verify app access permissions
   - Check Firebase Storage permissions

4. **Check Network:**
   - Verify internet connectivity
   - Check if Firebase Functions are accessible
   - Test with different network/browser

## Additional Resources
- Firebase Functions Documentation: https://firebase.google.com/docs/functions
- Cloud Function Logs: Firebase Console → Functions → Logs
- Stack Overflow: Search for "Firebase Functions CORS image"

Generated by Dine Platform Image Processor
`;

  const blob = new Blob([logContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `cloud-function-debug-${appData.id}-${Date.now()}.txt`;
  link.click();
  URL.revokeObjectURL(url);
};

// Export utility functions for easier testing and debugging
export const CloudFunctionUtils = {
  testImageGeneration,
  checkCloudFunctionStatus,
  generateIndividualImages,
  downloadCloudFunctionLogs
};